import { useSyncExternalStore, useMemo, useCallback, useEffect } from 'react';
import * as store from './notificationsStore';

const includesValue = (values, value) => {
  if (!values?.length) return true;
  return values.includes(value);
};

// Only the main channel drives the badge — scanner/debug traffic is noise.
const isMain = (n) => channelOf(n) === 'master';

export const matchesContext = (n, ctx = {}) => {
  if (!includesValue(ctx.brokers, n.broker)) return false;
  if (!includesValue(ctx.instruments, n.symbol)) return false;
  if (!includesValue(ctx.levels, n.level)) return false;
  if (!includesValue(ctx.accounts, n.account)) return false;
  // `broker|symbol` pairs: a folder mixing brokers must not match their cross product.
  if (!includesValue(ctx.pairs, `${n.broker}|${n.symbol}`)) return false;
  return true;
};

export const channelOf = (n) => n.channel || 'master';

// What a view asks the server for, on top of the scope. Null: nothing to ask
// (Favorites with no starred timeframe would otherwise mean "everything").
export const viewFilters = ({
  channel = 'all', filter = 'all', level = 'all', favorites = null, favoritesPair = null, favoriteStars = null,
} = {}) => {
  const f = {};
  if (channel === 'favorites' && favoriteStars) {
    // Several instruments: ask for the union of their timeframes and pairs; the
    // panel keeps only each instrument's own stars on each account.
    const stars = favoriteStars.filter((s) => s.timeframes?.length);
    if (!stars.length) return null;
    f.timeframe_metas = [...new Set(stars.flatMap((s) => s.timeframes))].sort();
    f.pairs = [...new Set(stars.map((s) => `${s.broker}|${s.symbol}`))].sort();
  } else if (channel === 'favorites') {
    if (!favorites?.length) return null;
    f.timeframe_metas = favorites;
    if (favoritesPair) f.pairs = [favoritesPair];
  } else if (channel !== 'all') {
    f.channels = [channel];
  }
  if (filter === 'flagged') f.only_flagged = true;
  if (level !== 'all') f.levels = [level];
  return f;
};

// Quick-mode view (several channels, Favorites narrowing them): the Favorites
// part as viewFilters builds it, plus every picked channel. Null as there.
export const quickView = ({ pickedChannels = [], favoritesOnly = false, ...rest } = {}) => {
  const f = viewFilters({ ...rest, channel: favoritesOnly ? 'favorites' : 'all' });
  if (f && pickedChannels.length) f.channels = [...pickedChannels].sort();
  return f;
};

// The view's newest FEED_SIZE among what is cached.
export const lastN = (items, predicate, n = store.FEED_SIZE) => items
  .filter(predicate)
  .sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0))
  .slice(0, n);

export default function useNotificationsStream(context = {}) {
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const contextKey = JSON.stringify(context || {});

  const scoped = useMemo(() => {
    const ctx = JSON.parse(contextKey);
    return snap.items.filter((n) => matchesContext(n, ctx));
  }, [snap.items, contextKey]);

  // The badge counts what the Main tab shows: unread among its last FEED_SIZE.
  useEffect(() => { store.ensure({ ...JSON.parse(contextKey), channels: ['master'] }); }, [contextKey]);
  const unread = useMemo(() => lastN(scoped, isMain).filter((n) => !n.is_read).length, [scoped]);

  const load = useCallback((view) => store.ensure({ ...JSON.parse(contextKey), ...view }), [contextKey]);

  const markAllRead = useCallback(() => {
    const ids = scoped.filter((n) => !n.is_read).map((n) => n.id);
    store.markRead(ids);
  }, [scoped]);

  const clearAll = useCallback(() => {
    const ctx = JSON.parse(contextKey);
    const ids = snap.items.filter((n) => matchesContext(n, ctx)).map((n) => n.id);
    store.clearScoped(ids);
  }, [contextKey, snap.items]);

  return {
    items: scoped,
    unread,
    loading: snap.loading,
    load,
    markRead: store.markRead,
    markAllRead,
    toggleFlag: store.toggleFlag,
    remove: store.remove,
    clearAll,
  };
}
